import React, { useState } from 'react';
import { Sensor } from '../../types';
import { Dialog } from '../common/Dialog';
import { logAuditEvent, AUDIT_ACTIONS } from '../../utils/auditLogger';
import { API_CONFIG } from '../../config/api.config';
import { prepareCommonHeaders } from '../../services/baseApi';

interface CalibrateSensorDialogProps {
  sensor: Sensor | null;
  onClose: () => void;
  /** Current user, passed through to the audit log entry. */
  user: any;
}

async function sendCalibration(sensorId: number, setpoint: string, offset: string) {
  const response = await fetch(`${API_CONFIG.baseURL}/calibrate`, {
    method: 'POST',
    credentials: 'include',
    headers: prepareCommonHeaders(),
    body: JSON.stringify({ sensorId, setpoint, offset }),
  });
  if (!response.ok) {
    throw new Error('Failed to send calibration');
  }
  return await response.json();
}

async function clearCalibration(sensorId: number) {
  const response = await fetch(`${API_CONFIG.baseURL}/calibrate`, {
    method: 'POST',
    credentials: 'include',
    headers: prepareCommonHeaders(),
    body: JSON.stringify({ sensorId, action: 'CLEAR' }),
  });
  if (!response.ok) {
    throw new Error('Failed to clear calibration');
  }
  return await response.json();
}

/** Sends a per-sensor calibration setpoint/offset over LoRa ("CAL:" command,
 * handleCalibrationCommand() in the sensor firmware) or clears it. Separate
 * from the shared LUT — this corrects one physical unit's own offset. */
export function CalibrateSensorDialog({ sensor, onClose, user }: CalibrateSensorDialogProps) {
  const [setpoint, setSetpoint] = useState('');
  const [offset, setOffset] = useState('');
  const [calLoading, setCalLoading] = useState(false);
  const [calError, setCalError] = useState<string | null>(null);
  const [calSuccess, setCalSuccess] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [clearSuccess, setClearSuccess] = useState(false);

  const reset = () => {
    setSetpoint('');
    setOffset('');
    setCalError(null);
    setCalSuccess(false);
    setClearError(null);
    setClearSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sensor) return;
    setCalLoading(true);
    setCalError(null);
    setCalSuccess(false);
    try {
      await sendCalibration(sensor.id, setpoint, offset);
      await logAuditEvent(
        AUDIT_ACTIONS.SENSOR_CALIBRATE,
        `Sensor calibrated - Sensor ID: ${sensor.id}, Name: ${sensor.name}, Setpoint: ${setpoint}, Offset: ${offset}`,
        user
      );
      setCalSuccess(true);
      setTimeout(handleClose, 1200);
    } catch (err: any) {
      setCalError(err.message || 'Failed to calibrate');
    } finally {
      setCalLoading(false);
    }
  };

  const handleClear = async () => {
    if (!sensor) return;
    setClearLoading(true);
    setClearError(null);
    setClearSuccess(false);
    try {
      await clearCalibration(sensor.id);
      await logAuditEvent(
        AUDIT_ACTIONS.SENSOR_CALIBRATE_CLEAR,
        `Sensor calibration cleared - Sensor ID: ${sensor.id}, Name: ${sensor.name}`,
        user
      );
      setClearSuccess(true);
      setTimeout(handleClose, 1200);
    } catch (err: any) {
      setClearError(err.message || 'Failed to clear calibration');
    } finally {
      setClearLoading(false);
    }
  };

  return (
    <Dialog isOpen={!!sensor} onClose={handleClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Calibrate Sensor</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
          Sensor: <span className="font-medium text-gray-900 dark:text-white">{sensor?.name}</span>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Setpoint</label>
          <input
            type="number"
            step="any"
            className="w-full border rounded px-2 py-1 text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
            value={setpoint}
            onChange={e => setSetpoint(e.target.value)}
            required
            disabled={calLoading || clearLoading}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Offset</label>
          <input
            type="number"
            step="any"
            className="w-full border rounded px-2 py-1 text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
            value={offset}
            onChange={e => setOffset(e.target.value)}
            required
            disabled={calLoading || clearLoading}
          />
        </div>
        {calError && <div className="text-red-600 dark:text-red-400 text-xs">{calError}</div>}
        {calSuccess && <div className="text-green-600 dark:text-green-400 text-xs">Calibration sent successfully!</div>}
        {clearError && <div className="text-red-600 dark:text-red-400 text-xs">{clearError}</div>}
        {clearSuccess && <div className="text-green-600 dark:text-green-400 text-xs">Calibration cleared successfully!</div>}
        <div className="flex justify-between space-x-2 mt-4">
          <div>
            <button
              type="button"
              className="px-3 py-1 rounded bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-60"
              onClick={handleClear}
              disabled={clearLoading || calLoading}
            >
              {clearLoading ? 'Clearing...' : 'Clear Calibration'}
            </button>
          </div>
          <div className="flex space-x-2">
            <button
              type="button"
              className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs"
              onClick={handleClose}
              disabled={calLoading || clearLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1 rounded bg-yellow-600 text-white text-xs font-semibold hover:bg-yellow-700 disabled:opacity-60"
              disabled={calLoading || clearLoading}
            >
              {calLoading ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
